import { defineMessages } from "react-intl";

export const MESSAGES = defineMessages({
  pageTitle: {
    id: "funding-application: page title",
    defaultMessage: "Connect all your bank accounts",
  },
  pageSubtitle: {
    id: "funding-application: page subtitle",
    defaultMessage: "We use your banking data to assess your application and build your offer.",
  },
  connectionCardTitle: {
    id: "funding-application: connection card title",
    defaultMessage: "Connect your bank",
  },
  connectionCardSubtitle: {
    id: "funding-application: connection speed",
    defaultMessage: "Instant verification, usually under an hour.",
  },
  recommendedBadge: {
    id: "funding-application: recommended badge",
    defaultMessage: "Recommended",
  },
  requiredBadge: {
    id: "funding-application: required badge",
    defaultMessage: "Required",
  },
  bankLocationLabel: {
    id: "funding-application: bank location label",
    defaultMessage: "Bank location",
  },
  connectButton: {
    id: "funding-application: connect button",
    defaultMessage: "Connect with {providerName}",
  },
  connectButtonGeneric: {
    id: "funding-application: connect button generic",
    defaultMessage: "Connect",
  },
  uploadCardTitle: {
    id: "funding-application: upload title",
    defaultMessage: "Upload statements",
  },
  bankStatementsSlideoutTitle: {
    id: "funding-application: bank statements slideout title",
    defaultMessage: "Upload bank statements",
  },
  uploadCardSubtitle: {
    id: "funding-application: upload description",
    defaultMessage: "Can take days, may require follow-up.",
  },
  uploadAvailableAfterBankConnection: {
    id: "funding-application: upload available after bank connection",
    defaultMessage: "Available after connecting your bank.",
  },
  uploadAreaLabel: {
    id: "funding-application: upload statements label",
    defaultMessage: "Upload statements",
  },
  needHelpHeading: {
    id: "funding-application: need help heading",
    defaultMessage: "Need help connecting?",
  },
  footerSupportText: {
    id: "funding-application: footer support text",
    defaultMessage:
      "<a1>Invite a team member</a1> to connect on your behalf, or <a2>talk to our team</a2> for support.",
  },
  ukBankingSatisfiedDisclosure: {
    id: "funding-application: uk banking satisfied disclosure",
    defaultMessage:
      "By clicking {nextStep} to continue, you confirm your details are correct. We may share your personal information with fraud prevention agencies, and if fraud is detected, you may be refused services, financing, or employment. Read our <privacyLink>Privacy Notice</privacyLink>.",
  },
  selectCountryError: {
    id: "funding-application: select country error",
    defaultMessage: "Select a country.",
  },
  platformsButton: {
    id: "funding-application: platforms button",
    defaultMessage: "Platforms",
  },
  documentsButton: {
    id: "funding-application: documents button",
    defaultMessage: "Documents",
  },
  softCreditCheckButton: {
    id: "funding-application: soft credit check button",
    defaultMessage: "Soft credit check",
  },
  requiredModalTitle: {
    id: "funding-application: required modal title",
    defaultMessage: "Connect a bank account to continue",
  },
  requiredModalBody: {
    id: "funding-application: required modal body",
    defaultMessage:
      "We need your banking data to assess your application. You can connect via open banking or upload your statements manually.",
  },
  slideoutRequirementBody: {
    id: "funding-application: slideout requirement body",
    defaultMessage:
      "Provide at least {months} months of bank statements from {date} onwards for every bank account associated with your business.",
  },
  slideoutWhatToIncludeHeading: {
    id: "funding-application: slideout what to include heading",
    defaultMessage: "Statements must:",
  },
  slideoutRule1: {
    id: "funding-application: slideout rule 1",
    defaultMessage: "Have no gaps longer than 14 days.",
  },
  slideoutRule2: {
    id: "funding-application: slideout rule 2",
    defaultMessage: "Include at least one transaction in the last 14 days.",
  },
  slideoutRule3: {
    id: "funding-application: slideout rule 3",
    defaultMessage: "Cover all accounts associated with your business.",
  },
  submitStatementsButton: {
    id: "funding-application: submit statements button",
    defaultMessage: "Submit statements",
  },
  bankAccountsConnected: {
    id: "funding-application: bank accounts connected",
    defaultMessage: "{count, plural, one {# account connected} other {# accounts connected}}",
  },
  manageAccounts: {
    id: "funding-application: manage accounts",
    defaultMessage: "Manage accounts",
  },
  connectionsPanelTitle: {
    id: "funding-application: connections panel title",
    defaultMessage: "Connect bank accounts",
  },
  connectionsPanelDescription: {
    id: "funding-application: connections panel description",
    defaultMessage: "Connect all accounts associated with your business for the most accurate offer.",
  },
  connectedAccountsHeading: {
    id: "funding-application: bank connections heading",
    defaultMessage: "Bank accounts",
  },
  accountEnding: {
    id: "funding-application: bank account mask",
    defaultMessage: "···{mask}",
  },
  addAnotherAccountHeading: {
    id: "funding-application: add another account heading",
    defaultMessage: "Connect bank accounts",
  },
  addAnotherAccountButton: {
    id: "funding-application: add another account button",
    defaultMessage: "Add another account",
  },
  doneButton: {
    id: "funding-application: done button",
    defaultMessage: "Save accounts",
  },
  requireConnectionModalTitle: {
    id: "funding-application: require connection modal title",
    defaultMessage: "Bank connection needed",
  },
  requireConnectionModalBody: {
    id: "funding-application: require connection modal body",
    defaultMessage:
      "We need a direct bank connection to process your application. Connect your bank account to get started.",
  },
  connectBankButton: {
    id: "funding-application: connect bank button",
    defaultMessage: "Connect bank",
  },
  statementsUploadedCount: {
    id: "funding-application: statements uploaded count",
    defaultMessage: "{count, plural, one {# file uploaded} other {# files uploaded}}",
  },
  manageStatementsButton: {
    id: "funding-application: manage statements button",
    defaultMessage: "Manage statements",
  },
  countryNotListedToast: {
    id: "funding-application: country not listed toast",
    defaultMessage:
      "Direct bank connection isn't available in your selected region. Upload your bank statements to continue.",
  },
  countryNotListedRequireConnectionToast: {
    id: "funding-application: country not listed require connection toast",
    defaultMessage: "Direct bank connection isn't available in your selected region.",
  },
  contactSupportAction: {
    id: "funding-application: contact support toast action",
    defaultMessage: "Contact support",
  },
  auOpenBankingBannerTitle: {
    id: "bankconnect: au open banking banner title",
    defaultMessage: "We use Basiq to connect with Open Banking",
  },
  auOpenBankingOptIn: {
    id: "bankconnect: opt in to connect open banking",
    defaultMessage: "You may need to <b>opt-in to data sharing</b> with your bank before connecting.",
    description:
      "Pre-connection message for Australian Open Banking, informing of the requirement to enable data sharing on the bank side",
  },
  auOpenBankingDataSharingLink: {
    id: "bankconnect: data sharing help link",
    defaultMessage: "See our <a>Data Sharing Help</a> page for details.",
    description: "Link to our Data Sharing Help page",
  },
  timeInBusinessBanner: {
    id: "funding-application: bank connect - time in business explainer",
    defaultMessage:
      "Based on your business model, you'll need at least <b>{months} months</b> of trading history to qualify for funding. Connect your bank now and we'll let you know when you're eligible.",
  },
});
